import * as React from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export interface ConfirmDialogProps {
  abierto: boolean
  onCerrar: () => void
  onConfirmar: () => void | Promise<void>
  titulo: string
  /**
   * QUÉ VA A PASAR, en el idioma del cliente. No "¿Estás seguro?": una pregunta que no dice
   * las consecuencias se contesta que sí sin leerla.
   */
  descripcion?: React.ReactNode
  textoConfirmar?: string
  textoCancelar?: string
  /** `destructiva` pinta el botón en rojo. Lo que borra datos tiene que verse distinto. */
  variante?: 'normal' | 'destructiva'
  /**
   * Escribir esta palabra para habilitar el botón. Solo para lo irreversible de verdad
   * (revertir una carga, vaciar una empresa): pedirlo en todo entrena a teclear sin leer.
   */
  confirmacionEscrita?: string
  cargando?: boolean
}

/**
 * Confirmación con consecuencias, en lugar de `window.confirm`.
 *
 * El portal usaba `window.confirm` y `window.prompt` para cosas que borran datos de un
 * cliente. Tres problemas concretos, no de estética: el navegador los recorta y los pinta
 * sin formato (así que la explicación larga no se lee), algunos navegadores los suprimen si
 * el usuario marcó "no volver a mostrar" —y entonces la acción NO se ejecuta y nadie sabe
 * por qué—, y no se pueden probar.
 */
export function ConfirmDialog({
  abierto,
  onCerrar,
  onConfirmar,
  titulo,
  descripcion,
  textoConfirmar = 'Confirmar',
  textoCancelar = 'Cancelar',
  variante = 'normal',
  confirmacionEscrita,
  cargando = false,
}: ConfirmDialogProps) {
  const [escrito, setEscrito] = React.useState('')
  React.useEffect(() => {
    if (abierto) setEscrito('')
  }, [abierto])

  const bloqueado =
    cargando || (Boolean(confirmacionEscrita) && escrito.trim().toLowerCase() !== confirmacionEscrita!.trim().toLowerCase())

  return (
    <Dialog open={abierto} onOpenChange={(v) => !v && onCerrar()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          {descripcion && <DialogDescription asChild><div className="text-caption text-subtle">{descripcion}</div></DialogDescription>}
        </DialogHeader>

        {confirmacionEscrita && (
          <div className="space-y-1.5">
            <Label htmlFor="confirmacion-escrita">
              Escribe <span className="font-semibold">{confirmacionEscrita}</span> para continuar
            </Label>
            <Input
              id="confirmacion-escrita"
              value={escrito}
              onChange={(e) => setEscrito(e.target.value)}
              autoComplete="off"
            />
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onCerrar} disabled={cargando}>
            {textoCancelar}
          </Button>
          <Button
            variant={variante === 'destructiva' ? 'destructive' : 'default'}
            onClick={() => void onConfirmar()}
            disabled={bloqueado}
          >
            {cargando ? 'Un momento…' : textoConfirmar}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ConfirmDialog
